import React, { useCallback } from 'react';
import { LampOn, Add, Grid1 as TableIcon } from '@/components/icons';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { isClassificationColoringOn } from '@/lib/classification-color';
import { capture } from '@/lib/posthog';
import { createTableRow, resolveTableHeaderColor } from '@/lib/table-utils';
import { TableBlock, TestBlock } from '@/types/report-payload';
import PlaceholderBlock from '../wizard/test-blocks/PlaceholderBlock';
import EditableSection from './EditableSection';
import { MeasureWrapper } from './MeasureWrapper';
import { PaginatedTableBlock } from './pagination/types';
import { getTableDescriptionMeasurementId, getTableMeasurementId } from './pagination/utils';
import { ReportFootnoteMarker } from './ReportFootnotes';
import TestTableEditor from './test-table-editor/TestTableEditor';
import { ReportData } from './types';
import type { ReportFootnote } from './footnotes';

interface TestTableProps {
    block: TableBlock;
    data: ReportData;
    disabled?: boolean;
    primaryColor?: string;
    onUpdate: (updates: Partial<ReportData> | ((prev: ReportData) => Partial<ReportData>)) => void;
    onBlockUpdate: (blockId: string | number, updates: Partial<TestBlock>) => void;
    onAddTest: (blockId: string | number, mode?: 'default' | 'inline') => void;
    onAddChartFromTable: (blockId: string) => void;
    measurementId?: string;
    footnotes?: ReportFootnote[];
}

const TestTable: React.FC<TestTableProps> = ({
    block,
    data,
    disabled = false,
    primaryColor,
    onUpdate: _onUpdate,
    onBlockUpdate,
    onAddTest,
    onAddChartFromTable,
    measurementId,
    footnotes,
}) => {
    const [isEditing, setIsEditing] = React.useState(false);
    const shouldRenderDescription = !(block as PaginatedTableBlock).paginationHideDescription;

    const tableConfig = data.customizations?.table;
    const headerColor = resolveTableHeaderColor(
        tableConfig?.header_color,
        data.customizations?.colors?.primary,
    );
    const textAlignment = tableConfig?.text_alignment || 'center';
    const colorClassifications = isClassificationColoringOn(tableConfig);

    // Memoize the update handler to keep editor callbacks stable.
    const handleTableUpdate = useCallback(
        (updates: Partial<TableBlock>) => {
            onBlockUpdate(block.id || block.order, updates);
        },
        [block.id, block.order, onBlockUpdate],
    );

    // block.rows check might be needed if initially empty?
    if (!block.rows || block.rows.length === 0) {
        return (
            <DropdownMenu>
                <DropdownMenuTrigger asChild disabled={disabled}>
                    <PlaceholderBlock
                        type="table"
                        label="Clique para selecionar instrumentos para esta tabela"
                        description={
                            <span className="flex items-center gap-1.5">
                                <LampOn className="size-4 shrink-0 text-yellow-500" />
                                <span>Dica: Você pode copiar tabelas do Excel ou Docs</span>
                            </span>
                        }
                        onClick={() => {}}
                    />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-[240px]">
                    <DropdownMenuItem
                        onClick={() => {
                            capture('report_instrument_add_clicked', {
                                surface: 'table_placeholder',
                            });
                            onAddTest(block.id || block.order);
                        }}
                    >
                        <Add className="mr-2 size-4" />
                        Novo Instrumento
                    </DropdownMenuItem>
                    <DropdownMenuItem
                        onClick={() => {
                            const columnCount = block.header?.cells?.length || 0;
                            const newRows = [createTableRow(columnCount)];
                            onBlockUpdate(block.id || block.order, {
                                rows: newRows,
                            });
                        }}
                    >
                        <TableIcon className="mr-2 size-4" />
                        Nova Tabela em Branco
                    </DropdownMenuItem>
                </DropdownMenuContent>
            </DropdownMenu>
        );
    }

    const descriptionContent = (
        <div className="relative mb-4 border-l-2 border-slate-100 pl-5">
            {!!footnotes?.length && (
                <ReportFootnoteMarker
                    footnotes={footnotes}
                    className="pointer-events-none absolute left-2 top-0.5"
                />
            )}
            <EditableSection
                content={block.description}
                onSave={(val) =>
                    onBlockUpdate(block.id || block.order, {
                        description: val,
                    })
                }
                placeholder="Clique para adicionar descrição ou análise (opcional)..."
                disabled={disabled}
                isEditing={isEditing}
                onEditingChange={setIsEditing}
                reportId={data.id}
                sectionId="tests"
            />
        </div>
    );

    const tableContent = (
        <div className="relative">
            {!shouldRenderDescription && !!footnotes?.length && (
                <ReportFootnoteMarker
                    footnotes={footnotes}
                    className="pointer-events-none absolute -top-3 left-0 z-10"
                />
            )}
            <TestTableEditor
                block={block}
                onUpdate={handleTableUpdate}
                primaryColor={primaryColor}
                headerColor={headerColor}
                textAlignment={textAlignment}
                colorClassifications={colorClassifications}
                disabled={disabled}
                onAddChart={() => block.id && onAddChartFromTable(block.id)}
                measurementId={measurementId}
            />
        </div>
    );

    return (
        <>
            {shouldRenderDescription &&
                (measurementId ? (
                    <MeasureWrapper id={getTableDescriptionMeasurementId(measurementId)}>
                        {descriptionContent}
                    </MeasureWrapper>
                ) : (
                    descriptionContent
                ))}

            {measurementId ? (
                <MeasureWrapper id={getTableMeasurementId(measurementId)}>
                    {tableContent}
                </MeasureWrapper>
            ) : (
                tableContent
            )}
        </>
    );
};

export default TestTable;
